/* API types.
 *
 * These mirror the Pydantic schemas under app/backend/app/features exactly.
 * They are written
 * by hand rather than generated because the generator would be one more moving
 * part in the build, and the surface is small enough to keep honest by review.
 *
 * If a field changes on the server, it changes here in the same commit.
 */

export interface Author {
  id: string
  name: string
  avatar_url: string | null
}

export type PostType = 'post' | 'testimony' | 'news'
export type Visibility = 'global' | 'church' | 'group'

/** An uploaded image. Dimensions are sent so the client can reserve the space
 *  before it loads — without them the card jumps when it arrives. */
export interface Media {
  id: string
  /** Null only for a video still being prepared, which is only ever sent to
   *  the person who uploaded it: nothing may play an unchecked upload. */
  url: string | null
  /** A post carries images or one video; a message or a devotion can carry
   *  a PDF too. */
  kind?: 'image' | 'document' | 'video'
  /** Anything short of 'ready' is its uploader's view alone. */
  status?: 'uploading' | 'processing' | 'ready' | 'failed'
  /** Video only: the frame shown before it plays, and how long it runs. */
  poster_url?: string | null
  duration_ms?: number | null
  width: number | null
  height: number | null
  /** Documents only — what to call the link. An image is shown, not named. */
  filename?: string | null
  byte_size?: number | null
}

/** "— feeling blessed". Resolved by the server, so no client owns the wording. */
export interface Feeling {
  kind: string
  label: string
  emoji: string
}

/** What a reader can say back, beyond a thumbs-up.
 *
 *  Five, and deliberately nothing jokey: the same button carried
 *  "congratulations on the baptism" and "I read your mother's diagnosis", and
 *  a thumbs-up under the second reads as careless. There is no laugh and no
 *  wow — the moment such a reaction exists it lands on the wrong post.
 */
export type ReactionKind = 'like' | 'pray' | 'heart' | 'praise' | 'comfort'

/** One offered reaction, as GET /reactions serves it. */
export interface ReactionKindInfo {
  kind: string
  label: string
  emoji: string
}

/** One person in the "who reacted" list. */
export interface Reactor {
  user_id: string
  name: string
  avatar_url: string | null
  kind: string
}

export interface Post {
  /** Set on items of the home feed, which also carries Prayer Requests. */
  kind?: 'post'
  id: string
  body: string
  post_type: PostType
  /** The church live event this post announces, and whether it is on now.
   *  Once it has ended, its replay is this post's video. */
  live?: { call_id: string; state: 'scheduled' | 'live' | 'ended' } | null
  /** The live's host turned comments off on its form. */
  comments_off?: boolean
  /** Share My Testimony: the answered Prayer Request this post is the story
      of. Set only on a testimony born from one. */
  prayer_request_id: string | null
  visibility: Visibility
  church_id: string | null
  church_name: string | null
  group_id: string | null
  /** Set on a group post so the card can badge its audience. */
  group_name: string | null
  /** Shown beside the author's name. Null on most posts. */
  feeling: Feeling | null
  author: Author
  like_count: number
  comment_count: number
  share_count: number
  liked_by_viewer: boolean
  /** Which reaction the viewer chose, null for none. `liked_by_viewer` stays
   *  beside it and is true for ANY kind. */
  viewer_reaction: string | null
  /** Whether this viewer may remove it — decided by the server: the author,
   *  or whoever runs the church or group it is in. A public post is its
   *  author's and the super admin's alone. */
  viewer_can_remove?: boolean
  /** Per kind, kinds nobody chose omitted — the footer draws what was actually
   *  used, so an empty kind cannot put a chip on the stack. */
  reaction_counts: Record<string, number>
  reaction_total: number
  media: Media[]
  created_at: string
  /** Set once somebody changed it. Shown BESIDE the time, not instead of it —
   *  when something was said still matters to the replies underneath. */
  edited_at: string | null
}

export interface Comment {
  id: string
  body: string
  /** The comment this answers, or null. Replies are one level deep. */
  parent_id: string | null
  author: Author
  created_at: string
  edited_at: string | null
  like_count: number
  liked_by_viewer: boolean
}

/** What PATCH /posts, /prayer-requests and /comments all answer with. */
export interface EditResult {
  id: string
  body: string
  edited_at: string
}

export interface CreateCommentResult extends Comment {
  comment_count: number
}

/** Red = Critical/Urgent · Yellow = Continuous/Long term · Green = Answered. */
export type PrayerState = 'red' | 'yellow' | 'green'

/** The client's four responses, in their wording. */
export type ResponseType = 'comment' | 'will_pray' | 'praying' | 'prayed'

/** The client's three tabs: All Prayer Requests | My Requests | My Prayers. */
export type PrayerTab = 'all' | 'mine' | 'praying'

/** The "Promise for you :" verse block. Chosen by the server from the request
 *  id, so web and mobile show the same verse and it never changes. */
export interface Promise_ {
  verse: string
  reference: string
}

export interface PrayerRequest {
  id: string
  body: string
  state: PrayerState
  visibility: Visibility
  church_id: string | null
  church_name: string | null
  group_id: string | null
  author: Author
  response_count: number
  comment_count: number
  viewer_response: ResponseType | null
  /** Per-action counts, for the numbers beside the four buttons.
   *  Optional: a client can outlive the API build that added it. */
  counts?: Record<ResponseType, number>
  /** What kind of help would meet this need — the author's own tag. */
  help_kind: string | null
  /** Whether the VIEWER follows this request — the card's Follow control. */
  following: boolean
  media: Media[]
  /** Null until the request is five minutes old — the client asked for the
   *  promise to be attached after 5 mins, so the block is simply absent
   *  before then rather than showing a placeholder. */
  promise: Promise_ | null
  created_at: string
  edited_at: string | null
}

/** One item of the home feed: a post, or a Prayer Request drawn with the
 *  Prayer page's card. */
export type FeedItem =
  | Post
  | (PrayerRequest & { kind: 'prayer_request' })

export function isPrayerItem(
  item: FeedItem,
): item is PrayerRequest & { kind: 'prayer_request' } {
  return item.kind === 'prayer_request'
}

/** Cursor page. `next_cursor` is null on the last page; treat it as opaque. */
export interface Page<T> {
  items: T[]
  next_cursor: string | null
  has_more: boolean
}

export interface LikeResult {
  liked: boolean
  like_count: number
}

/** The whole reaction state of a post after a write.
 *
 *  Extends LikeResult rather than replacing it, exactly as the server's
 *  ReactOut does: `liked` is true for any kind and `like_count` is every
 *  reaction, so the plain-toggle fields keep their old meaning.
 */
export interface ReactResult extends LikeResult {
  viewer_reaction: string | null
  reaction_counts: Record<string, number>
  reaction_total: number
}

export interface RespondResult {
  response_type: ResponseType | null
  counts: Record<ResponseType, number>
}

export type Role = 'member' | 'church_admin' | 'super_admin'

export interface Me {
  id: string
  first_name: string
  last_name: string | null
  email: string | null
  mobile: string | null
  role: Role
  church_id: string | null
  church_name: string | null
  mobile_verified: boolean
  email_verified?: boolean
  avatar_url: string | null
  birthday?: string | null
  gender?: string | null
  /** False for an account that never set one — it can set one. */
  has_password?: boolean
  /** The switches of the caller's own church. Null when they have no church,
   *  which reads as everything on. */
  features: ChurchFeatures | null
}

export interface Session {
  access_token: string
  refresh_token: string
  token_type?: string
  expires_in?: number
}

/* ------------------------------------------------------------ notifications */

export type NotificationType =
  | 'admin_request_approved'
  | 'admin_request_declined'
  | 'member_joined'
  | 'post_liked'
  | 'post_commented'
  | 'prayer_response'
  | 'group_invitation'
  | 'group_join_requested'
  | 'group_join_approved'
  | 'group_join_declined'
  // An event or poll was created in a group you are in, and — to whoever
  // created it — somebody answering one.
  | 'group_event_created'
  | 'group_poll_created'
  | 'group_event_answered'
  | 'group_poll_answered'
  // A prayer you prayed for (or followed) was answered, and its owner shared
  // the testimony. Routes to the post — the testimony is the news.
  | 'testimony_shared'
  // A church administrator wrote to the congregation. The only notification
  // somebody gets for who they ARE rather than something they did — which is
  // why it is the only one with an opt-out.
  | 'church_message'
  // A call was arranged and you are in its audience.
  | 'call_scheduled'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  body: string | null
  /** Where to go when tapped — a type + id, so the client owns its routing. */
  entity_type: string | null
  entity_id: string | null
  /** Where inside the entity to go — on a reply, the comment answered. */
  sub_entity_id: string | null
  read: boolean
  created_at: string
  actor: Author | null
}

export interface NotificationsPage extends Page<Notification> {
  unread_count: number
}

export interface UnreadCount {
  unread_count: number
}

/* ------------------------------------------------------------------ groups */

export interface Member {
  id: string
  name: string
  mobile: string | null
  email: string | null
  role: Role
  is_moderator: boolean
  /** A named role from the church's own list, e.g. "Moderator-1". */
  role_name: string | null
  /** A person can be in several — Youth AND Choir. */
  groups: string[]
  joined_at: string
}

/** A group inside a church — Youth Fellowship, Choir.
 *
 * The client's document calls this a "sub-group", and a church a "Group".
 * See docs/CLIENT_TERMINOLOGY.md for the mapping.
 */
export interface Group {
  id: string
  name: string
  description: string | null
  member_count: number
  created_at: string
}

/** One group's own page. `church_id` lets the client confirm the group belongs
 *  to the church being administered. */
export interface GroupDetail extends Group {
  church_id: string
}

/** One church in the public directory (GET /signup/churches/directory).
 *
 *  NOT the same shape as GET /signup/{slug}, which describes ONE church by its
 *  invite link and carries status/awaiting_admin instead. Kept separate rather
 *  than merged into an optional-everything type, so a missing field is a
 *  compile error rather than a runtime undefined.
 *
 *  `slug` is what POST /signup/{slug}/join keys on — without it the directory
 *  could be listed but not acted on. */
export interface PublicChurch {
  id: string
  name: string
  slug: string
  city: string | null
}

/** A group the signed-in person belongs to, from GET /groups/my-groups.
 *
 *  The member's counterpart to the admin listing: same shape, plus whether
 *  they moderate it. The admin routes are all permission-checked, so this is
 *  the only group listing a plain member can read. */
export interface MyGroup extends Group {
  is_moderator: boolean
  /** Where you stand in THIS group. my-groups returns every group in your
   *  church now, not only your own — that is what lets a member ask to join
   *  one the admin has not invited them to. */
  is_member: boolean
  has_request: boolean
}

/** One pending join request, as an approver sees it. */
export interface JoinRequestPerson {
  id: string
  user_id: string
  name: string
  mobile: string | null
  avatar_url: string | null
  created_at: string
}

export interface MyGroupsPage {
  items: MyGroup[]
  church_id: string | null
  church_name: string | null
}

export interface GroupEvent {
  /** The creator's id. Compare against me.id — never against a display name. */
  created_by_id?: string | null
  id: string
  title: string
  description: string | null
  location: string | null
  starts_at: string
  ends_at: string | null
  created_by: string | null
  going: number
  maybe: number
  not_going: number
  /** This viewer's own answer, so the buttons render in the right state. */
  my_rsvp: RsvpStatus | null
  created_at: string
  /** Whether people are checked in at this event. */
  take_attendance?: boolean
  attendance_occurrence_id?: string | null
}

export type RsvpStatus = 'going' | 'maybe' | 'not_going'

/** One person's answer to an event — the name behind the count. */
export interface RsvpPerson {
  id: string
  name: string
  avatar_url: string | null
  status: RsvpStatus
  created_at: string
}

/** One person's vote, and which option they picked. */
export interface Voter {
  id: string
  name: string
  avatar_url: string | null
  option_id: string
  option_label: string
  created_at: string
}

export interface PollOption {
  id: string
  label: string
  votes: number
}

export interface Poll {
  created_by_id?: string | null
  id: string
  question: string
  closes_at: string | null
  created_by: string | null
  total_votes: number
  my_option_id: string | null
  options: PollOption[]
  created_at: string
}

export interface PollTally {
  options: PollOption[]
  total_votes: number
  my_option_id: string | null
}

/** One section of the Roles checkbox list — "People", "Posts", and so on. */
export interface PermissionGroup {
  section: string
  permissions: { key: string; label: string }[]
}

/** A named set of permissions — the client's "Moderator-1".
 *
 *  NOT the same as `Role` above, which is the account role. This one is a
 *  church's own creation. */
/** One row of the Moderators tab, from GET /groups/{id}/moderators.
 *  Mobile and email are null for somebody who may not see members. */
export interface Moderator {
  id: string
  name: string
  mobile: string | null
  email: string | null
  role_id: string | null
  role_name: string | null
  /** The church's administrator: listed, not demoted from here. */
  is_admin: boolean
}

export interface ChurchRole {
  id: string
  name: string
  description: string | null
  permissions: string[]
}

/** An invitation as the inviting admin sees it. */
export interface Invitation {
  /** The shareable link. Null once accepted or declined — it no longer works. */
  link: string | null
  id: string
  mobile: string
  name: string | null
  status: 'pending' | 'accepted' | 'declined'
  created_at: string
}

/** What happened to every number submitted.
 *
 *  Per-number rather than a total: an admin who pasted 200 lines needs to know
 *  which ones did not go, and why. */
export interface InviteResult {
  invited: string[]
  already_member: string[]
  already_invited: string[]
  invalid: string[]
}

/** An invitation as the invitee sees it. */
export interface MyInvitation {
  id: string
  group_id: string
  group_name: string
  church_name: string
  created_at: string
}

/** The four numbers across the top of the Administration screen. */
export interface ChurchStats {
  members: number
  joined_this_month: number
  groups: number
  prayer_requests: number
  prayer_red: number
  moderators: number
  roles: number
}

/* ------------------------------------------------------- prayer: help etc */

/** The client's ten help options. Served by the API so web and mobile cannot
 *  drift, and the list can grow without shipping two apps. */
export interface HelpOption {
  kind: string
  /** What a HELPER reads — "I can provide Groceries". The client's wording. */
  label: string
  /** What an ASKER reads — "Groceries". The same need, other direction: an
   *  offer on the composer of the person who needs it reads backwards. */
  need_label: string
}

export interface PrayerComment {
  id: string
  body: string
  /** The comment this answers, or null. Replies are one level deep. */
  parent_id: string | null
  /** Set when the comment came from offering help, so it can be shown as an
   *  offer rather than an ordinary remark. */
  help_kind: string | null
  author: Author
  created_at: string
  edited_at: string | null
  like_count: number
  liked_by_viewer: boolean
}

/* ---------------------------------------------------------------- console --
 *
 * The Administration console's own shapes. It runs on its own host, signs in
 * separately, and is the only surface that sees churches it does not belong
 * to — so these types are deliberately kept apart from the member app's.
 *
 * VOCABULARY. The API calls a church a "group" (/groups), because the client's
 * own specification does. Everywhere a person can read it, we say church — see
 * docs/CLIENT_TERMINOLOGY.md. The type names below follow the UI, not the URL.
 */

/** A church's place in its lifecycle. `awaiting_admin` means the church exists
 *  but nobody has completed the admin registration, so it cannot be used yet. */
export type ChurchStatus = 'active' | 'on_hold' | 'deleted' | 'awaiting_admin'

/** What a church has switched on. Messages, calls and going live default to
 *  on; Events and Polls default to off, so a church opts in to those. */
export interface ChurchFeatures {
  send_individual_messages: boolean
  send_bulk_msgs: boolean
  audio_conf_calls: boolean
  video_conf_calls: boolean
  live_broadcasts: boolean
  events: boolean
  polls: boolean
}

/** A numbered page, as the churches list returns.
 *
 *  NOT the cursor-based `Page<T>` the member app's feeds use. The console pages
 *  by number because an administrator needs "84 churches, page 3 of 5" —
 *  a cursor can only offer "more", which cannot render a pager or a count. */
export interface NumberedPage<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

/** One row of the churches list. */
export interface ChurchSummary {
  id: string
  name: string
  city: string | null
  status: ChurchStatus
  plan: string
  admin_mobile: string | null
  admin_email: string | null
  /** Null until the church is active — there is nothing to hand out before
   *  then, and showing a dead link would be worse than showing none. */
  signup_url: string | null
  member_count: number
}

/** One church's full record, behind GET /groups/{id}. */
export interface ChurchDetail extends ChurchSummary {
  state: string | null
  address: string | null
  slug: string
  welcome_text: string | null
  signature_text: string | null
  members_can_see_others: boolean
  created_at: string
  approved_at: string | null
  features: ChurchFeatures
}

/** A church waiting to be approved, in the Requests queue.
 *
 *  Two ids, and they are not interchangeable: `request_id` is what you decide
 *  on, `church_id` is what the decision creates or releases. */
export interface ChurchRequest {
  request_id: string
  church_id: string
  church_name: string
  city: string | null
  plan: string
  admin_name: string
  admin_email: string | null
  admin_mobile: string | null
  /** Who the church was created for, so the claimant can be compared to them. */
  invited_mobile: string | null
  invited_email: string | null
  /** True when the claimant IS the invited admin, false when they are not, and
   *  null when the church named no contact so there is nothing to compare. */
  matches_invite: boolean | null
  requested_at: string
}

/** One of the client's six custom signup-field types. */
export type CustomFieldType =
  | 'text_box'
  | 'check_box'
  | 'drop_down'
  | 'date_time'
  | 'image'
  | 'number'

/** A field a church adds to its own signup form, on top of the standard ones. */
/** A church created for this person that nobody administers yet. */
export interface ChurchAwaitingMe {
  id: string
  name: string
  slug: string
  city: string | null
  /** The church they would leave by claiming, when they are in one. */
  leaving_church_name: string | null
  /** They have claimed it and are waiting on a Super Admin. */
  awaiting_approval: boolean
}

export interface CustomField {
  id: string
  label: string
  field_type: CustomFieldType
  is_required: boolean
  /** Only a drop_down has these; the API rejects them on any other type. */
  options: string[] | null
  sort_order: number
}

/** A field being defined, before the server has given it an id. */
export interface CustomFieldDraft {
  label: string
  field_type: CustomFieldType
  is_required: boolean
  options?: string[] | null
}

/* ---------------------------------------------------------------- messaging */

/** Who a bulk message is for — "by user type per church group", or people
 *  chosen by rules ('filter'). */
export type AudienceKind = 'church' | 'group' | 'group_role' | 'list' | 'filter'

/* ------------------------------------------------------- choosing by rules */

/** What people can be chosen by: Gender, Age, or a signup question. */
export type AudienceFieldType = 'gender' | 'age' | 'attendance' | CustomFieldType

export type AudienceOp =
  | 'is'
  | 'contains'
  | 'under'
  | 'over'
  | 'between'
  | 'before'
  | 'after'
  | 'ticked'
  | 'not_ticked'
  | 'any_of'
  | 'answered'
  | 'not_answered'
  | 'absent_of_last'
  | 'absent_from'

export interface AudienceField {
  /** "gender", "age", or the signup field's id. */
  key: string
  label: string
  type: AudienceFieldType
  options: string[] | null
  /** Attendance only: recent service days to choose from. */
  choices?: { value: string; label: string }[] | null
  /** The comparisons this field allows, in the order to offer them. */
  ops: AudienceOp[]
}

export interface AudienceRule {
  field: string
  op: AudienceOp
  value?: string | number | null
  values?: string[] | null
  min?: string | number | null
  max?: string | number | null
  /** Attendance only: "absent `value` of the last `of` service days". */
  of?: string | number | null
  /** Filled in by the server on what it stores. */
  label?: string
  type?: AudienceFieldType
}

export interface AudienceFilter {
  match: 'all' | 'any'
  rules: AudienceRule[]
}

export interface AudiencePreview {
  count: number
  sample: { id: string; name: string; avatar_url: string | null }[]
  /** Set when an age rule is used: people in scope with no birthday. */
  without_birthday: number | null
  label: string
}

/** One row of the Members table, with every answer they gave. */
export interface MemberRow {
  id: string
  name: string
  avatar_url: string | null
  mobile: string | null
  email: string | null
  role: Role
  role_id: string | null
  role_name: string | null
  groups: string[]
  gender: 'male' | 'female' | null
  age: number | null
  birthday: string | null
  /** Signup answers by field id. Absent means not answered. */
  answers: Record<string, string>
  joined_at: string | null
  /** Set on a family member without an account: the name of the person
   *  whose household they are in. */
  household_of?: string | null
  relation?: string | null
}

export interface MemberTable {
  items: MemberRow[]
  total: number
  page: number
  page_size: number
  fields: AudienceField[]
  label: string
}

export interface ChurchMessage {
  id: string
  church_id: string
  title: string
  body: string
  audience_kind: AudienceKind
  /** The rules, when audience_kind is 'filter'. */
  audience_filter?: AudienceFilter | null
  group_id: string | null
  group_name: string | null
  role_id: string | null
  role_name: string | null
  /** "Everyone in Youth Fellowship" — phrased by the server. */
  audience_label: string
  channels: { in_app?: boolean; push?: boolean; sms?: boolean }
  status: 'draft' | 'queued' | 'sending' | 'sent' | 'failed'
  /** People, not channel rows. */
  recipient_count: number
  delivered_count: number
  failed_count: number
  error: string | null
  /** Set while a channel has not gone out yet — today only SMS, which waits
   *  for the campaign engine. */
  waiting_on: string | null
  author_name: string | null
  /** What it carries. Empty on almost every message. */
  media: Media[]
  send_at: string | null
  sent_at: string | null
  created_at: string
}

/** One registered way of saying something on a channel that cannot take free
 *  text. Shared by every church — the church's own name is filled in as a
 *  variable, which is what lets one approval serve them all. */
export interface MessageTemplate {
  id: string
  name: string
  description: string | null
  channel: 'sms' | 'whatsapp'
  /** The registered text, with {#var#} where each value goes. */
  body: string
  /** Every blank, in order, including 'church'. */
  variables: string[]
  /** The blanks to actually ask for. Never includes the church's name. */
  asks_for: string[]
  media_header: 'image' | 'document' | null
  /** False while the carrier or Meta has not issued an identifier yet.
   *  Shown but not choosable, so the shape of the screen still reads. */
  is_registered: boolean
  is_active: boolean
}

/** Exactly what this church's members would receive. */
export interface TemplatePreview {
  rendered: string
  /** Whether the carrier would accept this as that template. */
  matches: boolean
}

/** Which channels reach this person. Everything is on until they say otherwise. */
export interface ChannelPreferences {
  in_app: boolean
  push: boolean
  sms: boolean
}

/** One person, one channel, one outcome — a row in the Logs tab. */
export interface MessageRecipient {
  id: string
  user_id: string
  name: string
  avatar_url: string | null
  channel: 'in_app' | 'push' | 'sms'
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'skipped'
  /** Why, in plain words. Null when it simply worked. */
  detail: string | null
  /** Only in-app can answer this. */
  read_at: string | null
  sent_at: string | null
  delivered_at: string | null
}

export interface ChannelStats {
  channel: 'in_app' | 'push' | 'sms'
  total: number
  sent: number
  delivered: number
  failed: number
  skipped: number
  pending: number
  /** Null except on in-app, where read_at makes it real. */
  read: number | null
  /** What this channel can honestly prove. The UI labels its column from
   *  this rather than calling an FCM acceptance a delivery. */
  confirms: 'read' | 'accepted' | 'delivered'
}

export interface MessageAnalytics {
  message_id: string
  title: string
  audience_label: string
  status: string
  sent_at: string | null
  total_contacts: number
  reached: number
  not_reached: number
  pending: number
  success_rate: number
  channel_breakdown: ChannelStats[]
  problems: { channel: string; status: string; count: number; reason: string }[]
}

/** How well a church can reach its people at all. */
export interface ChurchMessagingAnalytics {
  members: number
  push_reachable: number
  sms_reachable: number
  push_opted_out: number
  messages_sent: number
  people_written_to: number
  people_reached: number
  bell_entries: number
  bell_read: number
  read_rate: number
}

/** A verse and a thought, at the same time every morning. */
export interface Devotion {
  id: string
  church_id: string
  title: string
  body: string
  audience_kind: AudienceKind
  group_id: string | null
  group_name: string | null
  role_id: string | null
  role_name: string | null
  audience_label: string
  channels: { in_app?: boolean; push?: boolean; sms?: boolean }
  /** "07:00" — a time of day, not a moment. */
  send_time: string
  timezone: string
  /** Bitmask: Mon=1 Tue=2 Wed=4 Thu=8 Fri=16 Sat=32 Sun=64. */
  days: number
  /** "Every day", "Weekdays", "Mon, Wed, Fri" — phrased by the server. */
  days_label: string
  is_active: boolean
  last_sent_on: string | null
  sent_count: number
  author_name: string | null
  /** Attached once, sent every morning. */
  media: Media[]
  created_at: string
}

/* ------------------------------------------------------------------ calls
 *
 * The client drew these three forms by hand — audio, video and live. Every
 * field below is one of theirs; see docs/CALL_SCREENS_SPEC.md.
 */

export type CallKind = 'audio' | 'video' | 'live'
export type CallStatus = 'scheduled' | 'live' | 'ended'

export interface ScheduledCall {
  id: string
  church_id: string
  /** The same audiences a bulk message offers. */
  audience_kind: AudienceKind
  /** The rules, when audience_kind is 'filter'. */
  audience_filter?: AudienceFilter | null
  group_id: string | null
  group_name: string | null
  role_id: string | null
  role_name: string | null
  /** "Everyone in Youth Fellowship" — phrased by the server. */
  audience_label: string
  kind: CallKind
  title: string
  description: string | null

  /** Null means "Now" on the client's form. */
  starts_at: string | null
  is_immediate: boolean
  is_public: boolean
  recording: boolean

  moderator_id: string | null
  moderator_name: string | null
  /** Live only: 'all_admins' | 'creator'. */
  moderated_by: string | null

  /** Video only — the client drew each as its own ON/OFF. */
  host_video: boolean | null
  participant_video: boolean | null
  host_mic: boolean | null
  participant_mic: boolean | null
  max_participants: number | null

  /** Live only. */
  allow_chat: boolean | null
  allow_comments: boolean | null

  /** Null until a room is opened. */
  room_id: string | null
  started_at: string | null
  ended_at: string | null
  status: CallStatus

  /** Whoever arranged it. They lead the call unless somebody else was
   *  named to, which is why the room needs the id and not just the name. */
  /** Where the tape lives, once the call has ended and it recorded. */
  recording_key?: string | null
  /** Where the file can be fetched. Derived from the key, which is the
   *  durable one — a recordings host that moves breaks stored urls. */
  recording_url?: string | null
  created_by_id: string | null
  created_by_name: string | null
  created_at: string
}

/** One person in a call. */
export interface CallParticipant {
  user_id: string
  name: string
  avatar_url: string | null
  role: 'listener' | 'speaker'
  is_moderator: boolean
  /** Muted BY A MODERATOR. Muting your own microphone never reaches a
   *  server — it is between you and your device. */
  muted: boolean
  hand_raised: boolean
  /** How long they have waited, which is what makes a queue read as fair.
   *  Null unless their hand is up. */
  waiting_seconds: number | null
  /** Your own place in the queue, 1-based. Only ever set on `you`. */
  queue_position: number | null
  /** False until their audio actually connects. */
  connected: boolean
  /** The service's handle for this person's audio. Null after a rejoin,
   *  which is the browser's cue to offer again. */
  leg_id?: string | null
  /** Frames are genuinely arriving from them — the service's confirmation,
   *  which the browser cannot give. */
  live?: boolean
  /** Why their audio never connected. They are not coming. */
  join_failed?: string | null
  /** What the church itself calls them, from its own roles screen. Null is
   *  ordinary — plenty of people hold no role. */
  role_name: string | null
  role_id: string | null
}

/** A role held by somebody in the room, and how many hold it. */
export interface RolePresent {
  role_id: string
  name: string
  count: number
}

/** The room as ONE person sees it, which is not the same for everybody:
 *  a moderator gets the hand queue and the roster, an audience member does
 *  not. See RoomStateOut on the server for why. */
export interface RoomState {
  call: ScheduledCall
  you: CallParticipant | null
  may_moderate: boolean
  speakers: CallParticipant[]
  hands: CallParticipant[]
  /** The whole queue, of which `hands` is the front. */
  hands_total: number
  listeners: CallParticipant[]
  /** Which roles are in the room, for the filter tabs. */
  roles: RolePresent[]
  present: number
}

/** How a call did.
 *
 *  Reach is always a number. Turnout is null until the call has opened at
 *  least once — a call nobody could join has no attendance to report, and
 *  showing 0% would read as a failure rather than as "it has not run".
 */
export interface CallAnalytics {
  call_id: string
  title: string
  kind: CallKind
  audience_label: string
  status: string
  starts_at: string | null
  started_at: string | null
  ended_at: string | null
  duration_seconds: number | null

  /** The tape. Both null until the call has ended. */
  recording_key: string | null
  recording_url: string | null

  /** In the audience when it was arranged. Frozen; it does not drift. */
  invited: number
  /** Got a notification. One fewer than invited when the organiser is in
   *  the audience, which is not a delivery failure. */
  told: number
  opened: number
  open_rate: number | null

  attended: number | null
  turnout_rate: number | null
  raised_hand: number | null
  spoke: number | null
  median_seconds: number | null
}


/** One person on a call's guest list, and what they did. */
export interface CallAttendee {
  user_id: string
  name: string
  avatar_url: string | null
  role_name: string | null
  came: boolean
  joined_at: string | null
  minutes: number | null
  hands_raised: number
  spoke: boolean
}

/** One page of a call's guest list, with both sides counted. */
export interface CallAttendancePage {
  items: CallAttendee[]
  /** How many match — the side asked for, or both. */
  total: number
  came_total: number
  missed_total: number
  page: number
  page_size: number | null
}

/** A church live event, as the watch page or the host's studio sees it. */
export interface LiveEvent {
  id: string
  title: string
  description: string | null
  state: 'scheduled' | 'live' | 'ended'
  starts_at: string | null
  started_at: string | null
  ended_at: string | null
  is_public: boolean
  allow_chat: boolean
  allow_comments: boolean
  /** Video, or sound alone — the live form's "Video / Audio". */
  video: boolean
  audience_label: string
  host_name: string | null
  /** Whether the person asking may broadcast it. */
  may_host: boolean
  /** Video arriving right now — as distinct from the event being on, which
   *  it stays through a host's momentary drop-out. */
  on_air: boolean
  /** Signed for the person asking; a path on the API. Null until the first
   *  video has arrived. */
  stream_url: string | null
  watching: number
  replay: {
    status: 'processing' | 'ready' | 'failed'
    url: string | null
    poster_url: string | null
    duration_ms: number | null
    width: number | null
    height: number | null
  } | null
}

export interface LiveChatMessage {
  id: string
  body: string
  created_at: string
  user_id: string
  name: string
  avatar_url: string | null
}

/* ------------------------------------------------------------- attendance */

/** Where the church is, for checking in by phone. */
export interface ChurchPlace {
  name: string
  address: string | null
  latitude: number | null
  longitude: number | null
  radius_m: number
  is_set: boolean
}

/** 'chosen': members choose — only those who attend it regularly are expected. */
export type ServiceAudienceKind = 'church' | 'group' | 'filter' | 'chosen'

export interface ServiceSlot {
  id: string
  label: string
  /** "09:00" */
  starts_time: string
  ends_time: string
}

/** A weekly service — Sunday Service, with its English and Tamil times. */
export interface Service {
  id: string
  name: string
  kind: string
  /** Monday 0 … Sunday 6. */
  weekday: number | null
  audience_kind: ServiceAudienceKind
  group_id: string | null
  group_name: string | null
  audience_filter: AudienceFilter | null
  audience_label: string
  /** How many attend it regularly — members-choose services only. */
  regulars?: number | null
  slots: ServiceSlot[]
}

export interface ServiceInput {
  name: string
  weekday: number
  slots: { label: string; starts_time: string; ends_time: string }[]
  audience_kind: ServiceAudienceKind
  group_id?: string | null
  audience_filter?: AudienceFilter | null
}

export type SlotState = 'upcoming' | 'open' | 'over'

export interface DaySlot {
  id: string
  label: string
  starts_at: string
  ends_at: string
  state: SlotState
}

export interface CheckedIn {
  at: string
  slot_label: string | null
  people: { id: string; name: string; by_usher: boolean }[]
  can_undo: boolean
}

export interface TodayService {
  occurrence_id: string
  name: string
  kind: string
  date: string
  slots: DaySlot[]
  current_slot_id: string | null
  current_slot_label: string | null
  is_on: boolean
  checked_in: CheckedIn | null
}

export interface HouseholdPerson {
  id: string
  name: string
  relation: string | null
  is_me: boolean
}

export interface AttendanceToday {
  date: string
  church_name: string
  location_set: boolean
  services: TodayService[]
  household: HouseholdPerson[]
  /** Set while a service is on and this person has not checked in. */
  banner: {
    occurrence_id: string
    name: string
    slot_label: string | null
    slot_id: string | null
  } | null
}

export interface WeekItem {
  service_id: string
  name: string
  date: string
  slots: { label: string; starts_at: string; ends_at: string }[]
  away: boolean
  /** A service members choose, and whether this member attends regularly. */
  members_choose?: boolean
  regular?: boolean
}

export type AttendanceStatus = 'present' | 'excused' | 'absent' | 'pending'

export interface MyAttendance {
  items: {
    occurrence_id: string
    date: string
    name: string
    kind: string
    status: AttendanceStatus
    slot_label: string | null
    checked_in_at: string | null
  }[]
  summary: { service_id: string; name: string; present: number; of: number }[]
}

export interface AttendanceCounts {
  expected: number
  present: number
  visitors: number
  excused: number
  absent: number
  not_yet: number
}

export interface Occurrence {
  id: string
  service_id: string
  name: string
  kind: string
  date: string
  starts_at: string
  ends_at: string
  state: 'upcoming' | 'on' | 'over'
  counts: AttendanceCounts
}

export interface RollRow {
  id: string
  name: string
  avatar_url: string | null
  mobile: string | null
  household_of: string | null
  relation: string | null
  expected: boolean
  status: AttendanceStatus | 'not_expected'
  method: string | null
  slot_label: string | null
  marked_at: string | null
}

export interface Roll {
  items: RollRow[]
  total: number
  page: number
  page_size: number
  counts: AttendanceCounts
  slots: { id: string; label: string; starts_at: string; ends_at: string }[]
}

export interface OccurrenceReport {
  id: string
  service_id: string
  name: string
  kind: string
  date: string
  starts_at: string
  ends_at: string
  over: boolean
  event_id: string | null
  group_name: string | null
  counts: AttendanceCounts
  slots: { id: string; label: string; starts_at: string; ends_at: string; present: number }[]
  by_method: Record<string, number>
  rsvp_going_absent: number | null
}

export type FollowUpKind = 'absent' | 'excused' | 'rsvp_no_show'

export interface FollowUpRow {
  id: string
  name: string
  mobile: string | null
  avatar_url: string | null
  household_of: string | null
  relation: string | null
  missed_in_a_row: number
  reason: string | null
  excuse_kind: string | null
}

export interface FollowUpPage {
  items: FollowUpRow[]
  total: number
  page: number
  page_size: number
  kind: FollowUpKind
  /** False while the day is still on. */
  final: boolean
}

/** Somebody in a household who has no account of their own. */
export interface HouseholdMember {
  id: string
  first_name: string
  last_name: string | null
  name: string
  relation: string | null
  birthday: string | null
  gender: 'male' | 'female' | null
}

export interface Household {
  items: HouseholdMember[]
  relations: string[]
}

export interface HouseholdMemberInput {
  first_name: string
  last_name?: string | null
  relation?: string | null
  birthday?: string | null
  gender?: 'male' | 'female' | null
}
